import moment from 'moment';
import { UPPERCASE_REGEX } from '../constants/settings';
import { AppointmentRequest } from '../types/appointmentRequests';
import { CalendarEvent } from '../types/common';
import { SessionPeerIDs } from '../types/session';

// function to extract root paths from pathname declared here
export const rootPath = (pathname: string, isRootPath?: boolean): string => {
  let path: string = '';

  const dashboardPattern = /\//;
  const associateRequestsPattern = /\/associate-requests/;
  const associatesPattern = /\/associates/;
  const clientsPattern = /\/clients/;
  const packagesPattern = /\/packages/;
  const vacationsPattern = /\/vacations/;
  const servicesPattern = /\/services/;

  // appointment pages pattern
  const appointmentPattern = /\/appointment\/[a-zA-Z0-9_-]+$/;
  const appointmentBookingPattern = /\/appointment\/booking/;
  const appointmentListPattern = /\/appointment\/list/;

  // setup pages pattern
  const setupPattern = /^\/setup\/[a-zA-Z0-9_-]+$/;
  const configPattern = /setup\/configurations/;
  const rolesPattern = /setup\/roles/;
  const featuresPattern = /setup\/features/;
  const rolePermissionsPattern = /setup\/role-permissions/;
  const permissionPattern = /setup\/permissions/;
  const languagePattern = /setup\/languages/;
  const countriesPattern = /setup\/countries/;
  const provincesPattern = /setup\/provinces/;
  const citiesPattern = /setup\/cities/;
  const postalCodesPattern = /setup\/postal-codes/;
  const conversionPattern = /setup\/conversion/;

  // history pages pattern
  const historyPattern = /^\/history\/[a-zA-Z0-9_-]+$/;
  const historyAppointmentPattern = /\/history\/appointment/;
  const rescheduleHistoryPattern = /\/history\/reschedule/;
  const referralsHistoryPattern = /\/history\/referrals/;
  const retainHistoryPattern = /\/history\/retain/;
  const transactionHistoryPattern = /\/history\/transaction/;
  const purchaseHistoryPattern = /\/history\/purchase/;
  const billingHistoryPattern = /\/history\/bills/;

  // feedback pages pattern
  const feedbacksPattern = /^\/feedbacks\/[a-zA-Z0-9_-]+$/;
  const clientFeedbackPattern = /\/feedbacks\/client/;
  const associateFeedbackPattern = /\/feedbacks\/associate/;

  const retainAgreementsPattern = /\/retain-agreements/;
  const sentRemindersPattern = /\/sent-reminders/;
  const auditLogsPattern = /\/audit-logs/;
  const profilePattern = /\/profile/;
  const notificationsPattern = /\/notifications/;

  if (appointmentPattern.test(pathname) && isRootPath) path = 'appointments';
  else if (appointmentBookingPattern.test(pathname))
    path = 'appointment_booking';
  else if (appointmentListPattern.test(pathname)) path = 'appointment_list';
  else if (associateRequestsPattern.test(pathname)) path = 'associate_requests';
  else if (associatesPattern.test(pathname)) path = 'associates';
  else if (clientsPattern.test(pathname)) path = 'clients';
  else if (setupPattern.test(pathname) && isRootPath) path = 'setups';
  else if (configPattern.test(pathname)) path = 'configurations';
  else if (rolesPattern.test(pathname)) path = 'roles';
  else if (featuresPattern.test(pathname)) path = 'features';
  else if (rolePermissionsPattern.test(pathname)) path = 'role_permissions';
  else if (permissionPattern.test(pathname)) path = 'permissions';
  else if (languagePattern.test(pathname)) path = 'languages';
  else if (countriesPattern.test(pathname)) path = 'countries';
  else if (provincesPattern.test(pathname)) path = 'provinces';
  else if (citiesPattern.test(pathname)) path = 'cities';
  else if (postalCodesPattern.test(pathname)) path = 'postal_codes';
  else if (conversionPattern.test(pathname)) path = 'minute_conversion';
  else if (servicesPattern.test(pathname)) path = 'services';
  else if (packagesPattern.test(pathname)) path = 'packages';
  else if (vacationsPattern.test(pathname)) path = 'vacations';
  else if (historyPattern.test(pathname) && isRootPath) path = 'history';
  else if (historyAppointmentPattern.test(pathname)) path = 'appointment';
  else if (rescheduleHistoryPattern.test(pathname)) path = 'reschedule';
  else if (referralsHistoryPattern.test(pathname)) path = 'referrals';
  else if (retainHistoryPattern.test(pathname)) path = 'retain';
  else if (transactionHistoryPattern.test(pathname)) path = 'transaction';
  else if (purchaseHistoryPattern.test(pathname)) path = 'purchase';
  else if (billingHistoryPattern.test(pathname)) path = 'billing_info';
  else if (feedbacksPattern.test(pathname) && isRootPath) path = 'feedbacks';
  else if (auditLogsPattern.test(pathname)) path = 'audit_logs';
  else if (clientFeedbackPattern.test(pathname)) path = 'client_feedbacks';
  else if (associateFeedbackPattern.test(pathname))
    path = 'associate_feedbacks';
  else if (retainAgreementsPattern.test(pathname)) path = 'retain_agreements';
  else if (sentRemindersPattern.test(pathname)) path = 'sent_reminders';
  else if (profilePattern.test(pathname)) path = 'profile';
  else if (notificationsPattern.test(pathname)) path = 'notifications';
  else if (!pathname || dashboardPattern.test(pathname)) path = 'dashboard';

  return path;
};

// function to generate random session id declared here
export const generateSessionId = (): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz';

  const segments = [3, 4, 3];

  let result: string = '';

  for (let i = 0; i < segments.length; i++) {
    let segment: string = '';

    for (let j = 0; j < segments[i]; j++) {
      segment += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    result += segment + (i < segments.length - 1 ? '-' : '');
  }

  return result;
};

// function to create a range array declared here
export const createRangeArray = (start: number, stop: number) => {
  return Array.from(
    { length: stop - start + 1 },
    (value, index) => start + index
  );
};

// function to convert hexadecimal color to RGB declared here
export const convertHexToRGB = (color: string) => {
  const hex = color.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const rgb = r + ', ' + g + ', ' + b;

  return rgb;
};

// function to capitalize first letter of each word of a string declared here
export const capitalize = (str: string): string => {
  const words = str.split(' ');

  for (let i = 0; i < words.length; i++) {
    words[i] = words[i][0].toUpperCase() + words[i].slice(1);
  }

  return words.join(' ');
};

// function to show full name combining first, middle & last name declared here
export const showFullName = (
  firstName: string,
  middleName: string | null,
  lastName: string,
  isClient?: boolean
): string => {
  return middleName
    ? `${firstName} ${middleName} ${lastName}`
    : firstName && lastName
    ? `${firstName} ${lastName}`
    : isClient
    ? 'Anonymous Client'
    : 'Anonymous Associate';
};

// function to format feature name declared here
export const formatFeatureName = (featureName: string) => {
  return featureName.replace(UPPERCASE_REGEX, ' $1').trim();
};

// function to convert appointments into calender events declared here
export const convertAppointmentsToCalenderEvents = (
  appointments: Array<AppointmentRequest>,
  isClient: boolean
): Array<CalendarEvent> => {
  const today = moment().startOf('day');

  return appointments.map((appointment) => ({
    start: moment(`${appointment.date}T${appointment.start_time}`).toDate(),
    end: moment(`${appointment.date}T${appointment.end_time}`).toDate(),
    data: {
      title: `Meeting with ${
        !isClient
          ? appointment.client_full_name
          : appointment.associate_full_name
      }`,
      description: appointment.additional_info || 'N/A',
      status: appointment.is_complete
        ? 'Completed'
        : moment(appointment.date).isSame(today, 'day')
        ? 'Pending'
        : moment(appointment.date).isAfter(today)
        ? 'Upcoming'
        : 'Pending',
    },
  }));
};

// function to split completed and upcoming appointments declared here
export const splitUpcomingAndCompleted = (
  appointments: Array<AppointmentRequest>
) => {
  let completed: Array<AppointmentRequest> = [];
  let unattended: Array<AppointmentRequest> = [];
  let upcoming: Array<AppointmentRequest> = [];
  let running: Array<AppointmentRequest> = [];

  appointments.forEach((appointment) => {
    if (
      +new Date(`${appointment.date} ${appointment.start_time}`) - +new Date() <
        0 &&
      +new Date(`${appointment.date} ${appointment.end_time}`) - +new Date() < 0
    ) {
      if (appointment.is_complete) {
        completed.push(appointment);
      } else {
        unattended.push(appointment);
      }
    } else if (
      +new Date(`${appointment.date} ${appointment.start_time}`) - +new Date() <
        0 &&
      +new Date(`${appointment.date} ${appointment.end_time}`) - +new Date() > 0
    ) {
      running.push(appointment);
    } else {
      upcoming.push(appointment);
    }
  });

  return { completed, upcoming, unattended, running };
};

// function to compare peer ids declared here
export const comparePeerIds = (obj1: SessionPeerIDs, obj2: SessionPeerIDs) => {
  return (
    obj1.associate_peer_id === obj2.associate_peer_id &&
    obj1.client_peer_id === obj2.client_peer_id
  );
};

// function to return query configs declared here
export const returnQueryConfig = (enableCondition: boolean) => {
  return {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enableCondition,
  };
};

// generic function to show picture properly declared here
export const showPicture = (pictureURL: string): string => {
  if (pictureURL.includes('media')) {
    return `${process.env.REACT_APP_USER_SERVICE_PROD_API}/${pictureURL}`;
  }

  return `${process.env.REACT_APP_USER_SERVICE_PROD_API}/media/${pictureURL}`;
};

// function to convert number to locale string declared here
export const convertNumberToLocaleString = (
  value: number,
  min: number = 0,
  max: number = 2
): string => {
  return value.toLocaleString('en-US', {
    maximumFractionDigits: max,
    minimumFractionDigits: min,
  });
};

// handler function to stop all media stream tracks declared here
export const stopStreamTracksHandler = (
  stream: MediaStream | null,
  setStream: React.Dispatch<React.SetStateAction<MediaStream | null>>
) => {
  if (stream) {
    stream.getTracks().forEach((track) => {
      track.stop();
      stream.removeTrack(track);
    });

    setStream(null);
  }
};
